import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { useNavigate } from 'react-router-dom';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import LoadingSpinner from '../common/LoadingSpinner';
import '../../styles/profile.css';
import { useToast } from '../../hooks/useToast';
// import ErrorBoundaryTest from '../common/ErrorBoundaryTest';

const getConfidentialityText = () => {
  return `How we handle your data:
  
Your data is yours. We don't sell it, share it with third parties, or use it to train language models.

Sessions — Practice sessions and coaching are stored only to track progress, give your org insights, and improve your experience. Nothing else.
Access — Only people your account admin authorizes can see session data, and only for program oversight.
Retention & deletion — We follow your org's retention policy. Your admin can request deletion of anyone's data anytime.
AI conversations — Processed to generate responses. Our team may review interactions for quality, per our privacy policy.

Questions? Talk to your account admin or reach out to Interflexion support.`;
};

const ProfileView = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast(); // Add this
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [isEditingName, setIsEditingName] = useState(false);
  const [showConfidentiality, setShowConfidentiality] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  /**
   * Load user profile on mount
   */
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;
      
      try {
        const data = await apiService.get('/api/v1/users/profile', user);
        setUserProfile(data.data);
      } catch (err) {
        console.error('Error loading user profile:', err);
        // Don't show error - just use Firebase user data as fallback
      } finally {
        setProfileLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

useEffect(() => {
  if (userProfile) {
    setDisplayName(userProfile.displayName || '');
  }
}, [userProfile]);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/');
    }
  };

const handleSaveName = async () => {
  if (!displayName.trim()) {
    setError('Display name cannot be empty');
    return;
  }

  setIsSaving(true);
  setError(null);
  // setSuccessMessage(null);

  try {
    // DON'T Update Firebase Auth display name
    // await updateUserProfile({
    //   displayName: displayName.trim()
    // });

    // Update backend profile
    await apiService.put('/api/v1/users/profile', {
      display_name: displayName.trim()
    }, user);

    // setSuccessMessage('Display name updated successfully!');
    showToast('Display name updated successfully!', 'success');
    setIsEditingName(false);
    
    // setTimeout(() => setSuccessMessage(null), 3000);
  } catch (err) {
    console.error('Error updating display name:', err);
    setError('Failed to update display name. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

    // And update the cancel handler
  const handleCancelEdit = () => {
    setDisplayName(userProfile?.displayName || user?.displayName || '');
    setIsEditingName(false);
    setError(null);
    };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>-]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`At least ${minLength} characters`);
    }
    if (!hasUpperCase) {
      errors.push('One uppercase letter');
    }
    if (!hasLowerCase) {
      errors.push('One lowercase letter');
    }
    if (!hasNumbers) {
      errors.push('One number');
    }
    if (!hasSpecialChar) {
      errors.push('One special character');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: 'None', color: '#ccc' };

    const validation = validatePasswordStrength(password);
    const score = 5 - validation.errors.length;

    if (score === 5) return { strength: 100, label: 'Strong', color: '#22c55e' };
    if (score === 4) return { strength: 75, label: 'Good', color: '#84cc16' };
    if (score === 3) return { strength: 50, label: 'Fair', color: '#eab308' };
    if (score >= 1) return { strength: 25, label: 'Weak', color: '#f97316' };
    return { strength: 0, label: 'Very Weak', color: '#ef4444' };
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validate all fields filled
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    // Validate new password strength
    const validation = validatePasswordStrength(passwordData.newPassword);
    if (!validation.isValid) {
      setPasswordError(`Password must have: ${validation.errors.join(', ')}`);
      return;
    }

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate new password is different
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);

    try {
      // Reauthenticate user with current password
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      // Success
      setSuccessMessage('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      
      if (err.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (err.code === 'auth/weak-password') {
        setPasswordError('New password is too weak');
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setShowPasswordChange(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError(null);
  };

  if (profileLoading) {
    return (
      <div className="profile-view loading">
        <LoadingSpinner message="Loading profile..." />
      </div>
    );
  }

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  return (
    <div className="profile-view">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Profile</h1>
              <button 
                onClick={handleLogout} 
                className="btn btn-secondary btn-sm"
              >
                Sign Out
              </button>
                    {/* <ErrorBoundaryTest /> */}
        </div>

        {error && (
          <div className="profile-message error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="profile-message success-message">
            {successMessage}
          </div>
        )}

        <div className="profile-content">
          {/* Display Name */}
          <div className="profile-field">
            <label>Display Name</label>
            {isEditingName ? (
              <div className="editable-field">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="profile-input"
                  placeholder="Enter your display name"
                  autoFocus
                  disabled={isSaving}
                />
                <div className="edit-actions">
                  <button 
                    className="save-btn-small"
                    onClick={handleSaveName}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="cancel-btn-small"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-value editable"
                onClick={() => setIsEditingName(true)}
                title="Click to edit"
              >
                {displayName || 'Not set'}
              </div>
            )}
          </div>

          {/* Email */}
          <div className="profile-field">
            <label>Email</label>
            <div className="profile-value">
              {user?.email}
            </div>
          </div>

          {/* Organization */}
          {userProfile?.tenant && (
            <div className="profile-field">
              <label>Organization</label>
              <div className="profile-value">
                {userProfile.tenant.name}
              </div>
            </div>
          )}

          {/* Member Since */}
          {userProfile?.createdAt && (
            <div className="profile-field">
              <label>Member Since</label>
              <div className="profile-value">
                {new Date(userProfile.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          )}

          {/* Change Password */}
          <div className="profile-field">
            <label>Password</label>
            {!showPasswordChange ? (
              <button 
                className="change-password-btn"
                onClick={() => setShowPasswordChange(true)}
              >
                Change Password
              </button>
            ) : (
              <div className="password-change-form">
                {passwordError && (
                  <div className="password-error">
                    {passwordError}
                  </div>
                )}

                <form onSubmit={handlePasswordChange}>
                  <div className="password-field">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="profile-input"
                      placeholder="Enter current password"
                      disabled={isChangingPassword}
                    />
                  </div>

                  <div className="password-field">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="profile-input"
                      placeholder="Enter new password"
                      disabled={isChangingPassword}
                    />
                    {passwordData.newPassword && (
                      <div className="password-strength">
                        <div className="strength-bar-container">
                          <div 
                            className="strength-bar" 
                            style={{ 
                              width: `${passwordStrength.strength}%`,
                              backgroundColor: passwordStrength.color
                            }}
                          />
                        </div>
                        <span className="strength-label" style={{ color: passwordStrength.color }}>
                          {passwordStrength.label}
                        </span>
                      </div>
                    )}
                    <div className="password-requirements">
                      <p>Password must contain:</p>
                      <ul>
                        <li className={passwordData.newPassword.length >= 8 ? 'valid' : ''}>
                          At least 8 characters
                        </li>
                        <li className={/[A-Z]/.test(passwordData.newPassword) ? 'valid' : ''}>
                          One uppercase letter
                        </li>
                        <li className={/[a-z]/.test(passwordData.newPassword) ? 'valid' : ''}>
                          One lowercase letter
                        </li>
                        <li className={/\d/.test(passwordData.newPassword) ? 'valid' : ''}>
                          One number
                        </li>
                        <li className={/[!@#$%^&*(),.?":{}|<>-]/.test(passwordData.newPassword) ? 'valid' : ''}>
                          One special character
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="password-field">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="profile-input"
                      placeholder="Confirm new password"
                      disabled={isChangingPassword}
                    />
                  </div>

                  <div className="password-actions">
                    <button 
                      type="submit"
                      className="save-password-btn"
                      disabled={isChangingPassword}
                    >
                      {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                    </button>
                    <button 
                      type="button"
                      className="cancel-password-btn"
                      onClick={handleCancelPasswordChange}
                      disabled={isChangingPassword}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
          {/* Confidentiality Statement */}
          <div className="profile-field">
            <label>Privacy</label>
            <button
              className="change-password-btn"
              onClick={() => setShowConfidentiality(true)}
            >
              Confidentiality Statement
            </button>
          </div>
        </div>
      </div>

      {showConfidentiality && (
        <div className="confidentiality-overlay" onClick={() => setShowConfidentiality(false)}>
          <div className="confidentiality-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confidentiality-modal-body">
              <pre className="confidentiality-text">{getConfidentialityText()}</pre>
            </div>
            <div className="confidentiality-modal-footer">
              <button
                className="confidentiality-ok-btn"
                onClick={() => setShowConfidentiality(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileView;